import { ButtonProps } from "antd"
import { CMSStaticSection } from "./CMSStaticSection"

export interface CMSContext {
    _id?: {
        timestamp: number,
        date: string
    },
    tenant?: string,
    project?: string,
    logicId?: string,
    language?: string,
    configuration: CMSConfiguration
}

export interface CMSConfiguration {
    enabled: boolean,
    page?: CMSPage,
    section?: CMSSection
}

export interface Footer {
    customData?: CustomData | undefined;
    enabled?: boolean | undefined;
    title?: string | undefined;
    subTitle?: string | undefined;
    text?: string | undefined;
    options?: {
        [key: string]: unknown
    } | undefined;
    type?: string | rewardTypes | coreUserTypes | undefined;
    image?: CMSImage[] | undefined;
    cta?: CMSCTA[] | undefined;
    sections?: CMSSection[] | undefined;
    banner?: CMSBanner | undefined;
    filters?: CMSFilters | undefined;
    table?: CMSTable | undefined;
}

export interface Header extends Footer {}

export interface TemplateVas {
    customData?: CustomData | undefined;
    clusterLogicId?: string | undefined;
    backgroundLinear?: string | undefined;
    colorLinear?: string | undefined;
    background?: string | undefined;
    colorPrimary?: string | undefined;
    colorSecondary?: string | undefined;
    backgroundImage?: string | undefined;
    backgroundCta?: string | undefined;
    colorCta?: string | undefined;
}

export interface NavigationItem {
    customData?: CustomData | undefined;
    title?: string | undefined;
    scope?: string | undefined;
    scopeParameters?: Partial<Record<string, string>> | undefined;
    text?: string | undefined;
    size?: ButtonProps['size'] | undefined;
    color?: 'primary' | 'default' | 'secondary' | 'tertiary' | undefined;
    image?: CMSImage[] | undefined;
    type?: CTAType | undefined;
    href?: CMSHref | undefined;
    options?: CustomData | undefined;
}

export interface Structure {
    customData?: CustomData;
    footer?: Footer;
    header?: Header;
    mainNavigation?: NavigationItem[];
    userNavigation?: NavigationItem[];
    footerNavigation?: NavigationItem[];
    profileNavigation?: NavigationItem[];
    webviewNavigation?: NavigationItem[];
    clusterLogicId?: string;
    templateVas?: TemplateVas[];
}

export interface CMSPage {
    enabled: boolean,
    title?: string,
    subTitle?: string,
    text?: string,
    options?: {
        headerAlign?: 'left' | 'center',
        metaTags?: {
            title?: string,
            description?: string
        },
        [key: string]: any
    }
    description?: string,
    sections?: CMSStaticSection.Content[]
    banner?: CMSBanner | null
    wallet?: boolean;
    structure?: Structure[] | undefined;
}

type rewardTypes = 'ACCEPTANCE' | 'APPROVAL_FORM' | 'REWARD_FORM' | 'MAIN' | 'SECONDARY' | 'ACCEPTED' | 'DECLINED' | 'IW' | 'PDA' | 'DETAILS';
type coreUserTypes = 'BANNER' | 'AVATAR' | 'INFO' | 'LOCKED_FIELDS' | 'PASSWORD' | 'THANKYOU_PAGE';

export interface CMSSection {
    enabled?: boolean
    title?: string,
    text?: string,
    options?: {
        headerAlign?: 'left' | 'center',
        container?: 'fluid' | 'contained',  // fluid 100% container 944px
        background?: 'trasparent' | 'primary-lighter',
        [key: string]: unknown
    }
    type?: string | rewardTypes | coreUserTypes,
    subTitle?: string;
    image?: CMSImage[],
    cta?: CMSCTA[],
    sections?: CMSSection[],
    filters?: CMSFilters,
    table?: CMSTable,
    banner?: CMSBanner | null,
    finalizationExtraProps ?: {
        enabled?: boolean,
        thankyoupage ?: CMSSection
    }
}

export interface CMSFilters {
    [key: string]: CMSFilter;
}

export interface CMSFilter {
    label: string,
    placeholder: string[],
}

interface CMSTable {
    [key: string]: {
        label: string
    }
}

interface CustomData {
    [key: string]: any;
}


export interface CMSImage {
    src: string,
    breakpoint?: Breakpoint,
    alt?: string
}

export type BannerTypes = 'GENERIC' | 'PAGE_HEADER' | 'PROJECT_INTRO' | 'ADVERTISEMENT' | 'SECTION' | 'SPLITTED'
export interface CMSBanner extends Omit<CMSSection, 'sections' | 'filters' | 'tables'>{
    options?: {
        container?: 'fluid' | 'contained', // fluid 100% container 1440px
        fluidResponsive?: boolean,
        onClickInCta?: boolean,
        scope?: string,
        height?: number,
        type?: BannerTypes,
        align?: 'LEFT' | 'CENTER' | 'RIGHT',
        [key: string]: unknown,
        help?: Record<string, string>;
    }
}

export interface CMSGenericCMS {
    title?: string,
    text?: string,
    image: CMSImage,
}

export interface CMSProfileInfoOptions {
    allowEdit?: boolean
}
export interface CMSHref {
    type: 'EXTERNAL' | 'INTERNAL'
    value: string
    target?: '_blank' | '_self' | '_parent' | '_top' | 'framename'
}

export type CTAType = "LINK" | "BUTTON" | "IMAGE" | 'WRAPLESS'
type CTAUserScope = 'DOWNLOAD' | 'REGISTRATION' | 'FORGOT' | 'SUBMIT' | 'HELP' | 'LOGIN' | 'SAVE' | 'CANCEL' | 'HOME'
type CTARewardScope = 'REDEMPTION_NEXT_STEP' | 'CONTEST_DETAILS';

export interface CMSCTA {
    title?: string,
    scope?: string | CTAUserScope | CTARewardScope,
    scopeParameters?: Partial<Record<string, string>>;
    size?: ButtonProps['size']
    color?: 'primary' | 'default' | 'secondary' | 'tertiary'
    text?: string,
    image?: CMSImage,
    type: CTAType,
    href?: CMSHref,
    options?: {
        [key: string]: unknown
    };
}


export enum Breakpoints {
    XXXL = 'xxxl',
    XXL = 'xxl',
    XL = 'xl',
    LG = 'lg',
    MD = 'md',
    SM = 'sm',
    XS = 'xs',
    XXS = 'xxs',
}
export type Breakpoint = `${Breakpoints}`